import * as turf from '@turf/turf';
import { Nestable, CircleData } from '../model/summary';

/**
 * Common utilities for market areas calculation
 * Centralizes all duplicated logic related to markets
 */

/**
 * Creates a circular buffer for a market area based on its datacenters
 * @param market - The market with calculated circleData
 * @returns GeoJSON Feature of the circle
 */
export function createMarketBuffer(market: Nestable): GeoJSON.Feature {
  const point = turf.point([market.circleData.center[0], market.circleData.center[1]]);
  let ratio = market.circleData.ratio + ((market.circleData.ratio * 20) / 100);
  ratio = ratio <= 0 ? 1 : ratio;
  const buffered = turf.buffer(point, ratio, {units: 'miles'});
  return buffered;
}

/**
 * Calculates the center and radius of a market area based on its datacenters coordinates
 * @param coordinates - Array of coordinates [lon, lat]
 * @returns CircleData with center, points and radius
 */
export function calculateMarketCircleData(coordinates: number[][], market: Nestable): CircleData {
  if (!coordinates || coordinates.length === 0) {
    return null;
  }

  const marketPoints = turf.points(coordinates);
  const marketCenterComplex = turf.center(marketPoints);
  const marketCenter = [marketCenterComplex.geometry.coordinates[0], marketCenterComplex.geometry.coordinates[1]];
  
  let bigger = 0;
  for (const point of coordinates) {
    const line = turf.lineString([[point[0], point[1]], [marketCenter[0], marketCenter[1]]]);
    const distance = turf.length(line, {units: 'miles'});
    bigger = distance >= bigger ? distance : bigger;
  }

  return {
    center: marketCenter,
    points: coordinates as any,
    ratio: bigger,
    market: market
  };
}

/**
 * Processes a market to calculate its circleData based on entireData
 * @param market - Market with populated entireData
 */
export function processMarketCircleData(market: Nestable): void {
  if (market.entireData && market.entireData.length > 0) {
    const coordinates = market.entireData.map(building => [building.lon, building.lat]);
    market.circleData = calculateMarketCircleData(coordinates, market);
  }
}

/**
 * Calculates the center of a geometric feature (polygon)
 * @param feature - GeoJSON Feature 
 * @returns Point with the center
 */
export function getCenterPointFromFeature(feature: any): any {
  const bounds = [];
  for (const coor of feature.geometry.coordinates[0]) {
    bounds.push([coor[0], coor[1]]);
  }
  const points = turf.points(bounds);
  return turf.center(points);
}

/**
 * Creates a GeoJSON source for market areas
 * @param market - Market with circleData
 * @returns GeoJSON source object
 */
export function createMarketGeoJSONSource(market: Nestable): any {
  const buffered = createMarketBuffer(market);
  return {
    "type": "geojson",
    "data": buffered
  };
}
